import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { INSUMO_ESTADO_LABEL, INSUMO_ESTADO_COLOR } from "@/lib/constants";
import { fechaHoraCompleta } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { PackageSearch, CheckCircle2, XCircle, Loader2, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { PillDot } from "@/components/marca/Pills";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

export default function Insumos() {
  const { user, logout } = useAuth();
  const soloMantenimiento = user?.rol === "mantenimiento";
  const puedeAprobar = user?.rol === "admin";
  const puedeEntregar = ["admin", "dispatch", "mantenimiento"].includes(user?.rol);

  const [estado, setEstado] = useState("todos");
  const [tipo, setTipo] = useState("todos");
  const [choferId, setChoferId] = useState("todos");
  const [choferes, setChoferes] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rechazando, setRechazando] = useState(null);

  useEffect(() => { api.get("/choferes").then((r) => setChoferes(r.data)).catch(() => {}); }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (estado !== "todos") params.set("estado", estado);
      if (tipo !== "todos") params.set("tipo", tipo);
      if (choferId !== "todos") params.set("chofer_id", choferId);
      const { data } = await api.get(`/insumos?${params.toString()}`);
      setItems(data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [estado, tipo, choferId]);

  useEffect(() => { cargar(); }, [cargar]);

  const entregar = async (id) => {
    try {
      await api.post(`/insumos/${id}/entregar`);
      toast.success(t("Marcado como entregado"));
      cargar();
    } catch (e) { toast.error(apiError(e)); }
  };

  const aprobar = async (id) => {
    try {
      await api.post(`/insumos/${id}/aprobar`, { nota: "" });
      toast.success(t("Gasto aprobado"));
      cargar();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-4 lg:p-8 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-[26px] lg:text-[30px] leading-[1.05] text-ink" data-testid="page-title">{t("Insumos y gastos")}</h1>
          <p className="text-sm text-ink-3">{t("Pedidos de los choferes y gastos del camión")}</p>
        </div>
        {soloMantenimiento && (
          <Button data-testid="insumos-logout" variant="outline" className="h-10" onClick={logout}><LogOut className="h-4 w-4 mr-2" />{t(" Cerrar sesión")}</Button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3 bg-white rounded-xl border p-4">
        <div>
          <Label className="eyebrow">{t("Tipo")}</Label>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger data-testid="insumos-filtro-tipo" className="h-10 w-36 bg-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="todos">{t("Todos")}</SelectItem>
              <SelectItem value="pedido">{t("Pedido")}</SelectItem>
              <SelectItem value="gasto">{t("Gasto")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="eyebrow">{t("Estado")}</Label>
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger data-testid="insumos-filtro-estado" className="h-10 w-48 bg-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="todos">{t("Todos")}</SelectItem>
              {Object.entries(INSUMO_ESTADO_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="eyebrow">{t("Chofer")}</Label>
          <Select value={choferId} onValueChange={setChoferId}>
            <SelectTrigger data-testid="insumos-filtro-chofer" className="h-10 w-44 bg-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="todos">{t("Todos")}</SelectItem>
              {choferes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="insumos-tabla">
            <thead>
              <tr className="bg-surface-2 text-left eyebrow">
                <th className="px-4 py-3">{t("Fecha")}</th>
                <th className="px-4 py-3">{t("Tipo")}</th>
                <th className="px-4 py-3">{t("Chofer")}</th>
                <th className="px-4 py-3">{t("Camión")}</th>
                <th className="px-4 py-3">{t("Categoría")}</th>
                <th className="px-4 py-3">{t("Detalle")}</th>
                <th className="px-4 py-3">{t("Monto")}</th>
                <th className="px-4 py-3">{t("Estado")}</th>
                <th className="px-4 py-3 text-right">{t("Acciones")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EFECE6]">
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-ink-3">{t("Sin registros")}</td></tr>
              )}
              {items.map((i) => (
                <tr key={i.id} data-testid={`insumo-fila-${i.id}`} className="hover:bg-surface-2">
                  <td className="px-4 py-3 text-ink-2 whitespace-nowrap">{fechaHoraCompleta(i.created_at)}</td>
                  <td className="px-4 py-3 text-ink-2 capitalize">{i.tipo}</td>
                  <td className="px-4 py-3 text-ink-2">{i.chofer_nombre}</td>
                  <td className="px-4 py-3 text-ink-2">{i.camion || "—"}</td>
                  <td className="px-4 py-3 text-ink-2">{i.categoria_nombre}</td>
                  <td className="px-4 py-3 text-ink-2">
                    <div>{i.tipo === "pedido" ? i.cantidad : i.descripcion}</div>
                    {i.tipo === "pedido" && i.nota && <div className="text-xs text-ink-3">{t("Nota: {nota}", { nota: i.nota })}</div>}
                    {i.estado === "rechazado" && i.nota && <div className="text-xs text-danger">{t("Rechazo: {nota}", { nota: i.nota })}</div>}
                  </td>
                  <td className="px-4 py-3 text-ink-2">{i.monto != null ? `$${i.monto.toFixed(2)}` : "—"}</td>
                  <td className="px-4 py-3">
                    <PillDot data-testid={`insumo-estado-${i.id}`} color={INSUMO_ESTADO_COLOR[i.estado]}>{INSUMO_ESTADO_LABEL[i.estado]}</PillDot>
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    {i.tipo === "pedido" && i.estado === "pendiente" && puedeEntregar && (
                      <Button data-testid={`insumo-entregar-${i.id}`} size="sm" className="" onClick={() => entregar(i.id)}><CheckCircle2 className="h-4 w-4 mr-1" />{t(" Entregado")}</Button>
                    )}
                    {i.tipo === "gasto" && i.estado === "pendiente_aprobacion" && puedeAprobar && (
                      <>
                        <Button data-testid={`insumo-aprobar-${i.id}`} size="sm" className="" onClick={() => aprobar(i.id)}>{t("Aprobar")}</Button>
                        <Button data-testid={`insumo-rechazar-${i.id}`} size="sm" variant="outline" className="border-[#E8B9B8] text-danger" onClick={() => setRechazando(i)}>{t("Rechazar")}</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {rechazando && <RechazarDialog insumo={rechazando} onClose={() => setRechazando(null)} onDone={() => { setRechazando(null); cargar(); }} />}
    </div>
  );
}

function RechazarDialog({ insumo, onClose, onDone }) {
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);
  const guardar = async () => {
    if (!nota.trim()) return toast.error(t("Escribe una nota de rechazo"));
    setSaving(true);
    try {
      await api.post(`/insumos/${insumo.id}/rechazar`, { nota });
      toast.success(t("Gasto rechazado"));
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="text-danger flex items-center gap-2"><XCircle className="h-5 w-5" />{t(" Rechazar gasto")}</DialogTitle>
          <DialogDescription>{t("Escribe el motivo del rechazo. El chofer lo verá en su lista.")}</DialogDescription>
        </DialogHeader>
        <Textarea data-testid="insumo-rechazo-nota" placeholder={t("Motivo del rechazo")} value={nota} onChange={(e) => setNota(e.target.value)} rows={3} />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
          <Button data-testid="insumo-rechazo-confirmar" className="bg-danger hover:bg-[#A92825] text-white" onClick={guardar} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Rechazar")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
